import i18n from '@/i18n/i18n'
import RetailerPaymentService from '@/services/admin/RetailerPaymentService'
import { build_query, errorMessage } from '@/utilities/helpers'

const { t } = i18n.global

export const namespaced = true

export const state = {
  payments: null,
  meta: null,
  paymentsPayable: null,
  paymentsNettable: null,
  payOrders: null,
  payOrder: {},
  payAmounts: null,
  payAmount: {},
  orderPayments: {}
}

export const mutations = {
  SET_PAYMENTS(state, payments) {
    state.payments = payments.data
    state.meta = payments.meta
  },
  SET_ORDER_PAYMENTS(state, params) {
    let row = state.payments.find(payment => payment.id === params.retailerPaymentId)

    if (!row) {
      return
    }

    state.orderPayments[params.retailerPaymentId] = params.orderPayments
    row.sub = params.orderPayments
  },
  SET_PAYMENTS_PAYABLE(state, paymentsPayable) {
    state.paymentsPayable = paymentsPayable.data
  },
  SET_PAYMENTS_NETTABLE(state, paymentsNettable) {
    state.paymentsNettable = paymentsNettable.data
  },
  ADD_PAYMENTS(state, payments) {
    state.payments.push(...payments.data)
    state.meta = payments.meta
  },
  ADD_PAY_ORDERS(state, payOrder) {
    state.payOrders.push(payOrder)
  },
  ADD_PAY_AMOUNTS(state, payAmount) {
    state.payAmounts.push(payAmount)
  }
}

export const actions = {
  getPayments({ commit, dispatch }, params) {
    let newQuery = build_query(params.query, ['trashed'], null, '-created_at', [
      'has_payment_for_direct_cash_to_retailer_order'
    ])

    return new Promise((resolve, reject) => {
      RetailerPaymentService.getPayments(params.retailer, newQuery)
        .then(response => {
          if (params.query.page) {
            commit('ADD_PAYMENTS', response)
          } else {
            commit('SET_PAYMENTS', response)
          }
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.retailer_payment.retailer_payments')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getRetailerOrderPayments({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerPaymentService.getRetailerOrderPayments(params.retailerId, params.paymentId, params.withTrashed || false)
        .then(response => {
          commit('SET_ORDER_PAYMENTS', {
            orderPayments: response.data,
            retailerPaymentId: params.paymentId
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: `${t('store.shared.fetching_problem', {
              element: t('store.admin.retailer_payment.retailer_payments')
            })} ${errorMessage(err)}`
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getPayable({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      RetailerPaymentService.getPayable(id)
        .then(response => {
          commit('SET_PAYMENTS_PAYABLE', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.retailer_payment.payable_information')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getNettable({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      RetailerPaymentService.getNettable(id)
        .then(response => {
          commit('SET_PAYMENTS_NETTABLE', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.retailer_payment.netting_sales')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createPayOrders({ state, commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerPaymentService.createPayOrders(params.retailerId, params.orders, params.paidDate, params.notify)
        .then(response => {
          if (state.payOrders) {
            commit('ADD_PAY_ORDERS', response.data)
          }

          const notification = {
            type: 'success',
            message: t('store.shared.done', {
              element: t('store.admin.retailer_payment.payments')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.done_error', {
                element: t('store.admin.retailer_payment.payments')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createPayAmount({ commit, state, dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerPaymentService.createPayAmount(params.retailerId, params.amount, params.paidDate, params.notify)
        .then(response => {
          if (state.payAmounts) {
            commit('ADD_PAY_AMOUNTS', response.data)
          }
          const notification = {
            type: 'success',
            message: t('store.shared.done', {
              element: t('store.admin.retailer_payment.payments')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.done_error', {
                element: t('store.admin.retailer_payment.payments')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  netting({ commit, state, dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerPaymentService.netting(params.retailerId, params.nettingBody)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.done', {
              element: t('store.admin.retailer_payment.netting')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.done_error', {
                element: t('store.admin.retailer_payment.netting')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deletePayment({ commit, dispatch }, params) {
    return new Promise((resolve, reject) => {
      RetailerPaymentService.deletePayment(params.retailer_id, params.id)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('store.admin.retailer_payment.payment')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.delete_error', {
                element: t('store.admin.retailer_payment.payment')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(true)
        })
    })
  }
}
